/**
 * Accommodation Placeholder Images
 *
 * Provides type-specific placeholder images for accommodations without photos.
 * Uses Unsplash for high-quality stock images with blur effect.
 */

export interface PlaceholderConfig {
  imageUrl: string;
  searchTerm: string;
}

/**
 * Get placeholder image URL for accommodation type
 * Uses Unsplash Source API for random images
 */
const PLACEHOLDER_IMAGES: Record<number, PlaceholderConfig> = {
  // 1: 호텔
  1: {
    imageUrl: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&h=600&fit=crop',
    searchTerm: 'luxury hotel building',
  },
  // 2: 모텔
  2: {
    imageUrl: 'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=800&h=600&fit=crop',
    searchTerm: 'modern hotel exterior',
  },
  // 3: 민박
  3: {
    imageUrl: 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=800&h=600&fit=crop',
    searchTerm: 'korean traditional house hanok',
  },
  // 4: 게스트하우스
  4: {
    imageUrl: 'https://images.unsplash.com/photo-1555854877-bab0e564b8d5?w=800&h=600&fit=crop',
    searchTerm: 'cozy guesthouse',
  },
  // 5: 펜션
  5: {
    imageUrl: 'https://images.unsplash.com/photo-1449844908441-8829872d2607?w=800&h=600&fit=crop',
    searchTerm: 'cottage in nature',
  },
  // 6: 여인숙
  6: {
    imageUrl: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=800&h=600&fit=crop',
    searchTerm: 'small inn',
  },
  // 7: 산장
  7: {
    imageUrl: 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=800&h=600&fit=crop',
    searchTerm: 'mountain lodge',
  },
  // 8: 전통숙소
  8: {
    imageUrl: 'https://images.unsplash.com/photo-1555217851-6141535bd771?w=800&h=600&fit=crop',
    searchTerm: 'traditional accommodation',
  },
  // 9: 쉐어하우스
  9: {
    imageUrl: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800&h=600&fit=crop',
    searchTerm: 'shared house',
  },
  // 10: 리조트
  10: {
    imageUrl: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=800&h=600&fit=crop',
    searchTerm: 'resort',
  },
  // 11: 생활형숙박시설
  11: {
    imageUrl: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800&h=600&fit=crop',
    searchTerm: 'residence',
  },
  // 12: 기타숙박업
  12: {
    imageUrl: 'https://images.unsplash.com/photo-1445019980597-93fa8acb246c?w=800&h=600&fit=crop',
    searchTerm: 'accommodation',
  },
};

/**
 * Default placeholder for unknown types
 */
const DEFAULT_PLACEHOLDER: PlaceholderConfig = {
  imageUrl: 'https://images.unsplash.com/photo-1445019980597-93fa8acb246c?w=800&h=600&fit=crop',
  searchTerm: 'accommodation building',
};

/**
 * Get placeholder image configuration for accommodation type
 */
export function getPlaceholderImage(typeId?: number | null): PlaceholderConfig {
  if (!typeId) return DEFAULT_PLACEHOLDER;
  return PLACEHOLDER_IMAGES[typeId] || DEFAULT_PLACEHOLDER;
}

/**
 * Check if accommodation has a valid image
 */
export function hasValidImage(imageUrl?: string | null): boolean {
  return Boolean(imageUrl && imageUrl.trim() !== '');
}
